'use strict';

import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { format } from 'date-fns';
import Calendar from 'rc-calendar';
import DatePicker from 'rc-calendar/lib/Picker';
import ClickAwayListener from 'react-click-away-listener';
import { useFloating, offset, size } from '@floating-ui/react-dom';
import Modal from 'react-modal';
import { useDropzone } from 'react-dropzone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'classnames/bind';

import { appSelectors, appOperations } from '@/state/domains/app';
import { usersOperations, usersSelectors } from '@/state/domains/users';
import { assignmentsSelectors, assignmentsOperations } from '@/state/domains/assignments';
import { ordersSelectors, ordersOperations } from '@/state/domains/orders';

import SimpleSelect from '@/views/common/SimpleSelect';

import s from './styles.scss';
import m from '../styles.scss';
import 'rc-calendar/assets/index.css';

const dateFormat = 'YYYY-MM-DD';

const sx = cn.bind(s);
const mx = cn.bind(m);

const steps = {
	FILE: 0,
	IMPORT: 1,
	ERRORS: 2,
	ASSIGN: 3,
	CALENDAR: 4,
	CONFLICT: 5,
	OFFER_MORE: 6,
};

let timeoutId;

/*
<input
										type='text'
										className={ s.searchUsers }
										value={ user ? '' : searchQ }
										placeholder={
											user ? `${user.firstName} ${user.lastName}` : ''
										}
										onFocus={ onSearchFocus }
										onChange={ onSearchChange }
										autoFocus
									/>
									{ !!searchResults.length && (
										<div
											className={ s.searchResults }
											ref={ floating }
											style={{
												position: strategy,
												top: y ?? 0,
												left: x ?? 0,
												zIndex: 30,
											}}
										>
											{
												searchResults.map((u, i) => (
													<div
														key={ i }
														className={ s.searchItem }
														onClick={ () => {
															setSearchResults([]);
															setUser(u);
														}}
													>
														<div className={ s.name }>
															{ `${ u.firstName } ${ u.lastName }` }
														</div>
														<div className={ s.uname }>
															{ `(${ u.username })` }
														</div>
													</div>
												))
											}
										</div>
									)}
*/

export default connect(
	(state, props) => ({
		modals: appSelectors.getModalStates(state, props),
		assignments: assignmentsSelectors.getPageUserAssignments(state, props),
		loggedInUser: usersSelectors.getLoggedInUser(state, props),
		_users: usersSelectors.getAllUsers(state, props),
	}),
	{
		getUsers: usersOperations.getUsers,
		setModalClose: appOperations.setModalClose,
		importJobs: ordersOperations.postImportJobs,
		searchUsersReq: usersOperations.searchUsers,
		getAssignments: assignmentsOperations.getAssignments,
		postAssignJobs: ordersOperations.postAssignJobs,
	}
)(
	({
		modals,
		isOpen,
		loggedInUser,
		setModalClose,
		assignments,
		_users,
		importJobs,
		searchUsersReq,
		getAssignments,
		postAssignJobs,
		getUsers,
	}) => {

		const [ files, setFiles ] = useState([]);
		const [ company, setCompany ] = useState('slm');
		const [ importDate, setImportDate ] = useState(new Date());
		const [ formData, setFormData ] = useState({});
		const [ importedItems, setImportedItems ] = useState([]);
		const [ errors, setErrors ] = useState([]);
		const [ step, setStep ] = useState(steps.FILE);
		const [ searchQ, setSearchQ ] = useState('');
		const [ searchResults, setSearchResults ] = useState([]);
		const [ user, setUser ] = useState();//loggedInUser);
		const [ users, setUsers ] = useState([]);
		const [ userInputTouched, setUserInputTouched ] = useState(false);
		const [ isLoading, setIsLoading ] = useState(false);

		const mounted = useRef(false);

		useEffect(() => {
			mounted.current = true;

			return () => {
				mounted.current = false;
			};
		});

		useEffect(
			() => {
				getUsers({});
			},
			[]
		);

		useEffect(
			() => {
				setUsers(_users.map(u => ({
					label: `${ u.firstName } ${ u.lastName }`,
					value: u.username,
				})));
			},
			[ _users ]
		);

		useEffect(
			() => {
				if (!user) {
					setUser(users.find(u => u.value === loggedInUser.username));
				}
			},
			[ users ]
		);

		const {
			x,
			y,
			reference,
			floating,
			strategy,
			update,
		} = useFloating({
			placement: 'bottom',
			middleware: [
				offset(4),
				size({
					apply: (ctx) => {
						const { availableWidth, reference, elements } = ctx;
						Object.assign(elements.floating.style, {
							width: `${ elements.reference.offsetWidth }px`,
						});
					},
				}),
			],
		});

		const [ menuOpen, setMenuOpen ] = useState(false);
		const incStep = () => {
			setStep(step + 1);
		};

		const onDatePickerChange = importDate => {
			setImportDate(importDate.toDate());
		};

		const onDrop = files => {

			setFormData({
				...formData,
				file: files[ 0 ],
			});

			incStep();
		};

		const onCancel = () => {
			setStep(0);
			setModalClose('importJobs');
		};

		const onBack = () => {
			setStep(step - 1);
		};

		const onImportClick = () => {

			proceedImport();
		};

		const onSearchChange = e => {
			const { value } = e.target;
			setSearchQ(value);
			searchUsers(value);
		};

		const searchUsers = value => {
			
			if (timeoutId) {
				clearTimeout(timeoutId);
			}

			timeoutId = setTimeout(
				() => {
					searchUsersReq(value).then(r => {
						setSearchResults(r.data.users);
					});
				},
				1000
			);
		};

		const onSearchFocus = () => {

			if (userInputTouched) {
				setUser(null);
			}

			setUserInputTouched(true);
		};

		const onAssignClick = () => {

			const date = format(importDate, 'yyyy-MM-dd');
			setIsLoading(true);

			getAssignments(user.value, [ date ], true).then(
				result => {
					setIsLoading(false);
					if (result.success) {
						if (date in result.data.assignments) {
							return setStep(steps.CONFLICT);
						} else {
							assignJobs();
						}
					}
				},
				error => setIsLoading(false)
			);
		};

		const proceedImport = () => {

			let _formData = new FormData();

			_formData.append('file', formData.file);
			_formData.append('companyId', company === 'slm' ? 1 : 2);

			setIsLoading(true);

			importJobs(_formData, true).then(
				result => {
					setIsLoading(false);

					if (result.success) {
						setImportedItems(result.data.items);
						
						if (result.data.errors.length) {
							setErrors(result.data.errors);
							setStep(steps.ERRORS);
						}
						setStep(steps.ASSIGN);
					} else {
						setStep(steps.FILE);
					}
				},
				error => {
					setIsLoading(false);
					setStep(steps.FILE);
				}
			);

			//window[ `${ user.username }.${ date }.routes` ] = undefined;
		};

		const assignJobs = append => {

			let data = {};

			if (append) {
				data.append = true;
			}

			const date = format(importDate, 'yyyy-MM-dd');
			const tzOffset = format(new Date(), 'xxx');

			data.username = user.value;
			data.date = date;
			data.tzOffset = tzOffset;
			data.items = importedItems;
			data.route = true;

			setIsLoading(true);

			postAssignJobs(data, true).then(
				result => {
					setIsLoading(false);

					if (result.success) {
						setStep(steps.OFFER_MORE);
					}
				},
				error => setIsLoading(false)
			);
		};

		const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

		return (
			<Modal
				appElement={ document.getElementById('root') }
				overlayClassName={ m.modalOverlay }
				isOpen={ modals.importJobs }
				shouldCloseOnOverlayClick={ true }
				shouldCloseOnEsc={ true }
				onRequestClose={ onCancel }
				className={ m.modal }
			>
				<div className={ s.importCont }>
				{ isLoading &&
					<div className={ s.loader }>
						<FontAwesomeIcon icon='circle-notch' size='5x' spin />
					</div>
				}
				{ step === steps.FILE && 
					<>
						<header className={ s.header }>
							Import Jobs
						</header>
						<div className={ s.content }>
							<div className={ s.row }>
								<label>Company:</label>
								<div className={ s.companyBtns }>
									<button
										className={
											sx(
												'companyBtn',
												'slm',
												{ active: company === 'slm' }
											)
										}
										onClick={ () => setCompany('slm') }
									>
										SLM
									</button>
									<button
										className={
											sx(
												'companyBtn',
												'pcas',
												{ active: company === 'pcas' }
											)
										}
										onClick={ () => setCompany('pcas') }
									>
										PCAS
									</button>
								</div>
							</div>
							<div className={ s.dropzone } { ...getRootProps() }>
								<input { ...getInputProps() } />
								<FontAwesomeIcon icon='upload' size='lg' />
							</div>
						</div>
					</>
				}
				{ step == steps.IMPORT &&
					<div>
						<div className={ s.proceed }>
							Proceed with import?
						</div>
						<div className={ m.btns }>
							<button
								onClick={ onImportClick  }
								className={ m.proceed }
							>
								Import
							</button>
							<button
								onClick={ onCancel }
								className={ m.cancel }
							>
								Cancel
							</button>
						</div>
					</div>
				}
				{ step === steps.ERRORS &&
					<div className={ s.errors }>
						<table className={ sx('errorsTable', 'content') }>
							<tbody>
							{
								errors.map(
									e => (
										<tr>
											<td>{ e.companyId }</td>
											<td>{ e.jobId }</td>
											<td>{ e.message }</td>
										</tr>
									)
								)
							}
							</tbody>
						</table>
						<div className={ m.btns }>
							<button
								onClick={ () => setStep(steps.CALENDAR) }
								className={ m.proceed }
							>
								Assign
							</button>
							<button
								onClick={ onCancel }
								className={ m.cancel }
							>
								Done
							</button>
						</div>
					</div>
				}
				{ step === steps.ASSIGN &&
					<div>
						<header className={ s.header }>
							Import Successful
						</header>
						<div className={ m.btns }>
							<button
								onClick={ () => setStep(steps.CALENDAR) }
								className={ m.proceed }
							>
								Assign to user
							</button>
							<button
								onClick={ onCancel }
								className={ m.cancel }
							>
								Done
							</button>
						</div>
					</div>
				}
				{ step === steps.CALENDAR &&
					<div>
						<header className={ s.header }>
							Assign Jobs
						</header>
						<div className={ s.content }>
							<div className={ s.searchUser }>
								<div className={ s.searchUserLabel }>
									User
								</div>
								<div className={ s.searchUserCont } ref={ reference }>
									<SimpleSelect
										value={ user }
										options={ users }
										onChange={ u => setUser(u) }
									/>
									
								</div>
							</div>
							<div className={ s.calendarCont }>
								<div className={ s.calendarLabel }>
									Day
								</div>
								<Calendar
									showDateInput={ false }
									format={ dateFormat }
									value={ importDate }
									onChange={ onDatePickerChange }
								/>
							</div>
						</div>
						<div className={ m.btns }>
							<button
								onClick={ onAssignClick }
								className={ m.proceed }
							>
								Assign
							</button>
							<button
								onClick={ onCancel }
								className={ m.cancel }
							>
								Cancel
							</button>
						</div>
					</div>
				}
				{ step === steps.CONFLICT &&
					<div className={ s.conflict }>
						<p>Jobs already exist for this day</p>
						<div className={ m.btns }>
							<button
								onClick={ onBack }
								className={ m.back }
							>
								<FontAwesomeIcon icon='arrow-left' size='lg' />
							</button>
							<button
								onClick={ () => assignJobs(true) }
								className={ m.cancel }
							>
								Append
							</button>
							<button
								onClick={ () => assignJobs(false) }
								className={ m.proceedRed }
							>
								Overwrite
							</button>
						</div>
					</div>
				}
				{ step === steps.OFFER_MORE &&
					<div className={ s.conflict }>
						<header className={ s.header }>
							Assigned jobs successfully
						</header>
						<div className={ m.btns }>
							<button
								onClick={
									() => {
										setStep(steps.FILE)
										setUserInputTouched(false);
									}
								}
								className={ m.proceed }
							>
								Import more
							</button>
							<button
								onClick={ () => setModalClose('importJobs') }
								className={ m.cancel }
							>
								Done
							</button>
						</div>
					</div>
				}
				</div>
			</Modal>
		);
	}
);
